<script setup lang="ts">
import type { ZwChapterNumbering } from "../lib/zwConfig";
import {
  isJust,
  isNothing,
  just,
  nothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";
import { assertNever } from "../lib/assertNever";
import { useTea } from "../lib/tea";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  useTemplateRef,
  watch,
} from "vue";
import { scrollCueFor } from "../lib/scrollCue";
import {
  readProjectAssetDataUrl,
  removeProjectAsset,
} from "../lib/tauri/assets";
import { isTauri } from "../lib/runtime";
import type {
  ZwChapterHeadingStyle,
  ZwConfig,
  ZwCopyrightPage,
  ZwDatabaseFieldDef,
  ZwDatabaseRelationPair,
  ZwDedicationPage,
  ZwFonts,
  ZwGoals,
  ZwMetadata,
  ZwPageChrome,
  ZwParagraphFormatting,
  ZwPrintSize,
  ZwSceneFormatting,
  ZwStatusBar,
} from "../lib/zwConfig";
import ZwButton from "./ui/ZwButton.vue";
import ZwDialog from "./ui/ZwDialog.vue";
import ProjectSettingsMetadataSection from "./ProjectSettingsMetadataSection.vue";
import ProjectSettingsGoalsSection from "./ProjectSettingsGoalsSection.vue";
import ProjectSettingsUiSection from "./ProjectSettingsUiSection.vue";
import ProjectSettingsStylingSection from "./ProjectSettingsStylingSection.vue";
import ProjectSettingsDictionarySection from "./ProjectSettingsDictionarySection.vue";
import ProjectSettingsWorldbuildingSection from "./ProjectSettingsWorldbuildingSection.vue";
import ProjectSettingsFrontmatterSection from "./ProjectSettingsFrontmatterSection.vue";
import ProjectSettingsChronicleSection from "./ProjectSettingsChronicleSection.vue";
import {
  buildProjectSettingsSavePayload,
  initialProjectSettingsDraftModel,
  updateProjectSettingsDraftFn,
  type ProjectSettingsDraftCmd,
} from "./ProjectSettingsDialog.update";

export type { ProjectSettingsSectionId } from "./projectSettingsSectionId";
import type { ProjectSettingsSectionId } from "./projectSettingsSectionId";

const props = defineProps<{
  open: boolean;
  config: ZwConfig;
  /** Folder name shown when metadata title is empty. */
  folderName: string;
  /** Project root path — required for cover image import. */
  rootPath: Maybe<string>;
  /** Open directly to this section when the dialog opens. */
  initialSection: Maybe<ProjectSettingsSectionId>;
}>();

const emit = defineEmits<{
  close: [];
  save: [
    payload: {
      fonts: ZwFonts;
      printSize: ZwPrintSize;
      paragraphFormatting: ZwParagraphFormatting;
      chapterHeadingStyle: ZwChapterHeadingStyle;
      chapterNumbering: ZwChapterNumbering;
      actNumbering: ZwChapterNumbering;
      pageChrome: ZwPageChrome;
      sceneFormatting: ZwSceneFormatting;
      copyrightPage: ZwCopyrightPage;
      dedicationPage: ZwDedicationPage;
      metadata: ZwMetadata;
      dictionary: string[];
      statusBar: ZwStatusBar;
      goals: ZwGoals;
      databaseFields: ZwDatabaseFieldDef[];
      databaseRelationPairs: ZwDatabaseRelationPair[];
    },
  ];
}>();

type SectionId = ProjectSettingsSectionId;

let coverPreviewToken = 0;

const metadataSection =
  useTemplateRef<InstanceType<typeof ProjectSettingsMetadataSection>>(
    "metadataSection",
  );
const stylingSection =
  useTemplateRef<InstanceType<typeof ProjectSettingsStylingSection>>(
    "stylingSection",
  );
const dictionarySection =
  useTemplateRef<InstanceType<typeof ProjectSettingsDictionarySection>>(
    "dictionarySection",
  );

const sections: { id: SectionId; label: string }[] = [
  { id: "metadata", label: "Details" },
  { id: "frontmatter", label: "Front matter" },
  { id: "styling", label: "Formatting" },
  { id: "dictionary", label: "Dictionary" },
  { id: "worldbuilding", label: "Worldbuilding" },
  { id: "ui", label: "Interface" },
  { id: "chronicle", label: "Writing history" },
];

async function refreshCoverPreview(): Promise<void> {
  const relative = draftModel.value.meta.coverImage.trim();
  const root = props.rootPath;
  if (
    props.open === false ||
    relative.length === 0 ||
    isNothing(root) ||
    isTauri() === false
  ) {
    void dispatchDraft({ type: "SetCoverPreviewSrc", src: "" });
    return;
  }
  const token = ++coverPreviewToken;
  try {
    const src = await readProjectAssetDataUrl(root.value, relative);
    if (token === coverPreviewToken) {
      void dispatchDraft({ type: "SetCoverPreviewSrc", src });
    }
  } catch (error) {
    console.error("Failed to load cover preview", error);
    if (token === coverPreviewToken) {
      void dispatchDraft({ type: "SetCoverPreviewSrc", src: "" });
    }
  }
}

function focusSection(sectionId: ProjectSettingsSectionId): void {
  void nextTick(() => {
    if (sectionId === "metadata") metadataSection.value?.focus();
    else if (sectionId === "styling") stylingSection.value?.focus();
    else if (sectionId === "dictionary") dictionarySection.value?.focus();
  });
}

function runProjectSettingsCmd(cmd: ProjectSettingsDraftCmd): void {
  switch (cmd.kind) {
    case "focusSection":
      if (props.open) focusSection(cmd.section);
      return;
    case "refreshCoverPreview":
      void refreshCoverPreview();
      return;
    default:
      assertNever(cmd);
  }
}

const { model: draftModel, dispatch: dispatchDraft } = useTea(
  initialProjectSettingsDraftModel,
  updateProjectSettingsDraftFn,
  { runCmd: just(runProjectSettingsCmd) },
);

const coverPreviewSrc = computed(() => draftModel.value.coverPreviewSrc);

const section = computed({
  get: (): SectionId => draftModel.value.section,
  set: (next: SectionId): void => {
    void dispatchDraft({ type: "SectionChanged", section: next });
  },
});
const draftFonts = computed({
  get: () => draftModel.value.fonts,
  set: (fonts: ZwFonts) => {
    void dispatchDraft({ type: "FontsChanged", fonts });
  },
});
const draftParagraphFormatting = computed({
  get: () => draftModel.value.paragraphFormatting,
  set: (paragraphFormatting: ZwParagraphFormatting) => {
    void dispatchDraft({
      type: "ParagraphFormattingChanged",
      paragraphFormatting,
    });
  },
});
const draftActNumbering = computed({
  get: () => draftModel.value.actNumbering,
  set: (actNumbering: ZwChapterNumbering) => {
    void dispatchDraft({ type: "ActNumberingChanged", actNumbering });
  },
});
const draftChapterNumbering = computed({
  get: () => draftModel.value.chapterNumbering,
  set: (chapterNumbering: ZwChapterNumbering) => {
    void dispatchDraft({ type: "ChapterNumberingChanged", chapterNumbering });
  },
});
const draftChapterHeadingStyle = computed({
  get: () => draftModel.value.chapterHeadingStyle,
  set: (chapterHeadingStyle: ZwChapterHeadingStyle) => {
    void dispatchDraft({
      type: "ChapterHeadingStyleChanged",
      chapterHeadingStyle,
    });
  },
});

const draftPrintSize = computed({
  get: () => draftModel.value.printSize,
  set: (printSize: ZwPrintSize) => {
    void dispatchDraft({ type: "PrintSizeChanged", printSize });
  },
});
const draftPageChrome = computed({
  get: () => draftModel.value.pageChrome,
  set: (pageChrome: ZwPageChrome) => {
    void dispatchDraft({ type: "PageChromeChanged", pageChrome });
  },
});
const draftSceneFormatting = computed({
  get: () => draftModel.value.sceneFormatting,
  set: (sceneFormatting: ZwSceneFormatting) => {
    void dispatchDraft({ type: "SceneFormattingChanged", sceneFormatting });
  },
});
const draftCopyright = computed({
  get: () => draftModel.value.copyright,
  set: (copyright: ZwCopyrightPage) => {
    void dispatchDraft({ type: "CopyrightChanged", copyright });
  },
});
const draftDedication = computed({
  get: () => draftModel.value.dedication,
  set: (dedication: ZwDedicationPage) => {
    void dispatchDraft({ type: "DedicationChanged", dedication });
  },
});
const draftMeta = computed({
  get: () => draftModel.value.meta,
  set: (meta: ZwMetadata) => {
    void dispatchDraft({ type: "MetaChanged", meta });
  },
});
const draftCoverImage = computed({
  get: (): string => draftModel.value.meta.coverImage,
  set: (coverImage: string) => {
    void dispatchDraft({
      type: "MetaChanged",
      meta: { ...draftModel.value.meta, coverImage },
    });
  },
});
const draftDictionary = computed({
  get: () => draftModel.value.dictionary,
  set: (dictionary: string[]) => {
    void dispatchDraft({ type: "DictionaryChanged", dictionary });
  },
});
const draftStatusBar = computed({
  get: () => draftModel.value.statusBar,
  set: (statusBar: ZwStatusBar) => {
    void dispatchDraft({ type: "StatusBarChanged", statusBar });
  },
});
const draftDatabaseFields = computed({
  get: () => draftModel.value.databaseFields,
  set: (databaseFields: ZwDatabaseFieldDef[]) => {
    void dispatchDraft({ type: "DatabaseFieldsChanged", databaseFields });
  },
});
const draftDatabaseRelationPairs = computed({
  get: () => draftModel.value.databaseRelationPairs,
  set: (databaseRelationPairs: ZwDatabaseRelationPair[]) => {
    void dispatchDraft({
      type: "DatabaseRelationPairsChanged",
      databaseRelationPairs,
    });
  },
});
const draftSessionWords = computed({
  get: () => draftModel.value.sessionWords,
  set: (sessionWords: string) => {
    void dispatchDraft({ type: "SessionWordsChanged", sessionWords });
  },
});
const draftDailyWords = computed({
  get: () => draftModel.value.dailyWords,
  set: (dailyWords: string) => {
    void dispatchDraft({ type: "DailyWordsChanged", dailyWords });
  },
});
const draftProjectWords = computed({
  get: () => draftModel.value.projectWords,
  set: (projectWords: string) => {
    void dispatchDraft({ type: "ProjectWordsChanged", projectWords });
  },
});
const goalsFieldsKey = computed(() => draftModel.value.goalsFieldsKey);
const dictionaryAddDraft = computed({
  get: () => draftModel.value.dictionaryAddDraft,
  set: (dictionaryAddDraft: string) => {
    void dispatchDraft({
      type: "DictionaryAddDraftChanged",
      dictionaryAddDraft,
    });
  },
});

watch(
  () => props.open,
  (open) => {
    if (open === false) return;
    void dispatchDraft({
      type: "SeedFromConfig",
      config: props.config,
      initialSection: props.initialSection,
    });
  },
);

// If settings reopen before writeZwConfig finishes, fill empty goal drafts once
// the saved targets land on `config`.
watch(
  () => props.config.goals,
  (goals) => {
    if (props.open === false) return;
    void dispatchDraft({
      type: "SeedGoalsIfEmpty",
      goals: toMaybe(goals),
    });
  },
);

/** Narrow tab row scrolls sideways with a hidden scrollbar; fade the edges
 * that still hide tabs so the later sections are discoverable (ZW-276). The
 * cue is pure presentation of the element's own scroll position, so it is
 * written to the nav's dataset rather than the draft model. */
const navEl = useTemplateRef<HTMLElement>("navEl");
function updateNavCue(): void {
  const el = navEl.value;
  if (el === null) return;
  el.dataset.scrollCue = scrollCueFor({
    scrollLeft: el.scrollLeft,
    clientWidth: el.clientWidth,
    scrollWidth: el.scrollWidth,
  });
}
const navResize =
  typeof ResizeObserver === "undefined"
    ? null
    : new ResizeObserver(() => updateNavCue());
watch(
  navEl,
  (el, prev) => {
    if (prev instanceof HTMLElement) navResize?.unobserve(prev);
    if (el instanceof HTMLElement) navResize?.observe(el);
    updateNavCue();
  },
  { flush: "post" },
);
onBeforeUnmount(() => navResize?.disconnect());

async function save(): Promise<void> {
  // Finalize any in-progress contenteditable edit before reading drafts.
  const active = document.activeElement;
  if (active instanceof HTMLElement && active.isContentEditable) {
    active.blur();
    await nextTick();
  }

  const payload = buildProjectSettingsSavePayload(draftModel.value);

  const root = props.rootPath;
  if (isJust(root) && isTauri() === true) {
    const oldCover = props.config.metadata.coverImage.trim();
    const newCover = payload.metadata.coverImage;
    if (oldCover.length > 0 && oldCover !== newCover) {
      try {
        await removeProjectAsset(root.value, oldCover);
      } catch (error) {
        console.error("Failed to remove old cover image", error);
      }
    }
    const oldHeadingImage = props.config.chapterHeadingStyle.image.trim();
    const newHeadingImage = payload.chapterHeadingStyle.image;
    if (oldHeadingImage.length > 0 && oldHeadingImage !== newHeadingImage) {
      try {
        await removeProjectAsset(root.value, oldHeadingImage);
      } catch (error) {
        console.error("Failed to remove old chapter title image", error);
      }
    }
  }

  emit("save", payload);
  emit("close");
}

function cancel(): void {
  emit("close");
}
</script>

<template>
  <ZwDialog
    :open="open"
    title="Project settings"
    size="xl"
    layout="sheet"
    show-close
    :description="nothing"
    @close="cancel"
  >
    <div class="project-settings-body">
      <nav
        ref="navEl"
        class="project-settings-nav"
        aria-label="Settings sections"
        @scroll.passive="updateNavCue"
      >
        <button
          v-for="item in sections"
          :key="item.id"
          type="button"
          class="project-settings-nav-btn"
          :class="{ active: section === item.id }"
          :aria-current="section === item.id ? 'true' : undefined"
          @click="section = item.id"
        >
          {{ item.label }}
        </button>
      </nav>

      <div class="project-settings-panel zw-scroll">
        <template v-if="section === 'metadata'">
          <ProjectSettingsMetadataSection
            ref="metadataSection"
            v-model:meta="draftMeta"
            v-model:cover-image="draftCoverImage"
            :folder-name="folderName"
            :root-path="rootPath"
            :cover-preview-src="coverPreviewSrc"
          />
        </template>

        <template v-else-if="section === 'ui'">
          <ProjectSettingsUiSection v-model:status-bar="draftStatusBar" />
        </template>

        <template v-else-if="section === 'frontmatter'">
          <ProjectSettingsFrontmatterSection
            v-model:copyright="draftCopyright"
            v-model:dedication="draftDedication"
            :meta-pub-date="draftMeta.pubDate"
            :meta-publisher="draftMeta.publisher"
            :meta-author="draftMeta.author"
          />
        </template>

        <template v-else-if="section === 'dictionary'">
          <ProjectSettingsDictionarySection
            ref="dictionarySection"
            v-model:dictionary="draftDictionary"
            v-model:add-draft="dictionaryAddDraft"
          />
        </template>

        <template v-else-if="section === 'worldbuilding'">
          <ProjectSettingsWorldbuildingSection
            v-model:fields="draftDatabaseFields"
            v-model:relation-pairs="draftDatabaseRelationPairs"
          />
        </template>

        <template v-else-if="section === 'chronicle'">
          <ProjectSettingsChronicleSection
            :root-path="rootPath"
            :project-title="
              draftMeta.title.trim().length > 0
                ? draftMeta.title.trim()
                : folderName
            "
            :active="open && section === 'chronicle'"
          />
          <ProjectSettingsGoalsSection
            v-model:session-words="draftSessionWords"
            v-model:daily-words="draftDailyWords"
            v-model:project-words="draftProjectWords"
            :fields-key="goalsFieldsKey"
          />
        </template>

        <template v-else-if="section === 'styling'">
          <ProjectSettingsStylingSection
            ref="stylingSection"
            v-model:fonts="draftFonts"
            v-model:print-size="draftPrintSize"
            v-model:paragraph-formatting="draftParagraphFormatting"
            v-model:chapter-heading-style="draftChapterHeadingStyle"
            v-model:chapter-numbering="draftChapterNumbering"
            v-model:act-numbering="draftActNumbering"
            v-model:page-chrome="draftPageChrome"
            v-model:scene-formatting="draftSceneFormatting"
            :root-path="rootPath"
          />
        </template>
      </div>
    </div>

    <template #footer>
      <ZwButton @click="cancel">Cancel</ZwButton>
      <ZwButton variant="primary" @click="save">Save</ZwButton>
    </template>
  </ZwDialog>
</template>

<style>
.project-settings-body {
  display: grid;
  grid-template-columns: 10rem minmax(0, 1fr);
  min-height: 0;
  flex: 1;
  height: 100%;
}

.project-settings-nav {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.75rem 0.55rem;
  border-right: 1px solid color-mix(in oklch, var(--line) 85%, transparent);
  background: color-mix(in oklch, var(--bg) 65%, var(--bg-raised));
}

.project-settings-nav-btn {
  appearance: none;
  border: 0;
  background: transparent;
  text-align: left;
  padding: 0.45rem 0.55rem;
  border-radius: 0.3rem;
  font: inherit;
  font-size: 0.85rem;
  color: var(--text-muted);
  cursor: pointer;
}

.project-settings-nav-btn:hover {
  color: var(--text-strong);
  background: var(--hover-fill);
}

.project-settings-nav-btn.active {
  color: var(--text-strong);
  background: color-mix(in oklch, var(--color-zw-amber) 14%, transparent);
}

.project-settings-panel {
  min-height: 0;
  overflow: auto;
  padding: var(--space-xl);
  display: flex;
  flex-direction: column;
  gap: var(--space-xl);
}

.project-settings-group {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
}

.project-settings-group + .project-settings-group {
  padding-top: var(--space-xl);
  border-top: 1px solid color-mix(in oklch, var(--line) 85%, transparent);
}

.project-settings-hint {
  margin: 0;
  max-width: 60ch;
  font-size: 0.8rem;
  line-height: 1.5;
  color: var(--text-muted);
}

.project-settings-export {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
  padding-top: 0.35rem;
}

.project-settings-export-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.project-settings-subhead,
.project-settings-group-title {
  margin: 0;
  font-family: var(--font-heading);
  font-size: 1.05rem;
  font-weight: 500;
  line-height: 1.4;
  color: var(--text-strong);
}

.project-settings-label {
  margin: var(--space-sm) 0 0;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text);
}

.project-settings-panel .zw-field-label {
  font-size: 0.78rem;
  letter-spacing: 0;
  text-transform: none;
}

.project-settings-panel
  :is(.zw-input, .zw-input-face-text, .zw-select, .zw-checkbox, .zw-btn) {
  font-size: 0.85rem;
}

.project-settings-panel .zw-field-row {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-md);
}

.project-settings-panel
  .zw-field-row:not(.chapter-numbering-row):has(> :only-child) {
  grid-template-columns: minmax(0, 1fr);
}

.project-settings-panel .zw-field-row > * {
  min-width: 0;
}

@media (max-width: 520px) {
  .project-settings-panel .zw-field-row:not(.chapter-numbering-row) {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (pointer: coarse) {
  .project-settings-panel :is(.zw-select, .zw-btn, .zw-checkbox) {
    min-height: 44px;
  }
  .project-settings-panel .zw-checkbox {
    align-items: center;
  }
  .project-settings-panel :is(.zw-field-label, .project-settings-hint) {
    font-size: 0.8rem;
  }
}

.project-settings-checks {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0.15rem 0 0.1rem;
}

.project-settings-goals-fields {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
}

.project-settings-goal-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.55rem;
  align-items: end;
}

.project-settings-copyright-fields {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
}

.project-settings-credits {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.project-settings-credits-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.project-settings-credits-head .project-settings-subhead {
  margin: 0;
}

.project-settings-credit-row {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 0.65rem;
  align-items: end;
}

.project-settings-credit-remove {
  margin-bottom: 0.1rem;
}

.project-settings-dictionary-add {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.65rem;
  align-items: end;
}

.project-settings-dictionary-add-btn {
  margin-bottom: 0.1rem;
}

.project-settings-dictionary-list {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin-top: 0.35rem;
}

.project-settings-dictionary-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.65rem;
  align-items: end;
}

.project-settings-dictionary-remove {
  margin-bottom: 0.1rem;
}

.project-settings-database-fields {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin-top: 0.35rem;
}

.project-settings-database-field-row {
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1.5fr) auto;
  gap: 0.65rem;
  align-items: end;
}

@media (max-width: 520px) {
  .project-settings-credit-row {
    grid-template-columns: 1fr;
  }

  .project-settings-credit-remove {
    justify-self: start;
  }

  .project-settings-dictionary-add,
  .project-settings-dictionary-row,
  .project-settings-database-field-row {
    grid-template-columns: 1fr;
  }

  .project-settings-dictionary-add-btn,
  .project-settings-dictionary-remove {
    justify-self: start;
  }
}

.project-settings-export .project-settings-hint {
  margin: 0;
}

.project-settings-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.86rem;
  padding: 0.05em 0.3em;
  border-radius: 0.2rem;
  background: color-mix(in oklch, var(--bg) 70%, var(--bg-raised));
  color: var(--text);
}

.project-settings-kbd {
  display: inline-block;
  min-width: 1.1em;
  padding: 0.05em 0.28em;
  border: 1px solid var(--line);
  border-radius: 0.2rem;
  background: var(--bg-raised);
  font-family: var(--font-ui);
  font-size: 0.78em;
  line-height: 1.3;
  text-align: center;
  color: var(--text-muted);
}

.zw-dialog-footer .zw-btn-primary {
  min-width: 4.5rem;
  font-weight: 600;
}

@media (max-width: 520px) {
  .project-settings-body {
    grid-template-columns: 1fr;
    grid-template-rows: auto minmax(0, 1fr);
  }

  .project-settings-nav {
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    border-right: 0;
    border-bottom: 1px solid color-mix(in oklch, var(--line) 85%, transparent);
    padding: 0.55rem 0.65rem;
    gap: 0.25rem;
  }

  .project-settings-nav-btn {
    flex: 0 0 auto;
    white-space: nowrap;
  }

  .project-settings-panel {
    padding: 1rem 1.1rem 1.15rem;
  }
}

html:is([data-zw-mobile-shell="1"], [data-zw-narrow="1"])
  .project-settings-body {
  grid-template-columns: 1fr;
  /* Tabs take their own height; the panel gets the rest (ZW-276). Without
     explicit rows a short panel let the tab row stretch to fill the sheet. */
  grid-template-rows: auto minmax(0, 1fr);
  min-height: 0;
  flex: 1;
}

/* One scrolling row of tabs with the native scrollbar hidden; the edge fade
   below is the scroll cue. */
html:is([data-zw-mobile-shell="1"], [data-zw-narrow="1"])
  .project-settings-nav {
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  border-right: 0;
  border-bottom: 1px solid color-mix(in oklch, var(--line) 85%, transparent);
  padding: 0.55rem 0.65rem;
  gap: 0.25rem;
}

html:is([data-zw-mobile-shell="1"], [data-zw-narrow="1"])
  .project-settings-nav-btn {
  flex: 0 0 auto;
  white-space: nowrap;
}

.project-settings-nav::-webkit-scrollbar {
  display: none;
}

/* Alpha masks only (no colour): fade whichever edge still hides tabs. */
.project-settings-nav[data-scroll-cue="end"] {
  mask-image: linear-gradient(to right, black calc(100% - 2.5rem), transparent);
}

.project-settings-nav[data-scroll-cue="start"] {
  mask-image: linear-gradient(to left, black calc(100% - 2.5rem), transparent);
}

.project-settings-nav[data-scroll-cue="both"] {
  mask-image: linear-gradient(
    to right,
    transparent,
    black 2.5rem,
    black calc(100% - 2.5rem),
    transparent
  );
}
</style>
